import React from "react";
import DefaultLayout from "../layouts/default";
import { graphql } from "gatsby";
import { EventType } from "../types/Types";
import { buildEventJsonLd } from "../utils/jsonLd";
import ContentBlocks from "../components/ContentBlocks";
import EventBlock from "../components/EventBlock";

export const EventsQuery = graphql`
  fragment pagesPagesFragmentEvents on CMS_pages_Entry {
    id
    title
    seoTitle
    url
    overviewDescription
    seoDescription
    overviewButtonText
    overviewButtonLink
    uri
    typeHandle
    backgroundColor
    backgroundColorCustom
    backgroundImage {
      id
      title
      url
      alt
    }
    overviewImage {
      id
      title
      url
      alt
    }
  }

  fragment contentBlocksFragmentEvents on CMS_events_Entry {
    contentBlocks {
      ... on CMS_contentBlockMenuCardBlock_Entry {
        ...contentBlockMenuCardBlockFragmentEvents
      }
      ... on CMS_contentBlockCatBlockRooms_Entry {
        ...contentBlockCatBlockRoomsFragmentEvents
      }
      ... on CMS_contentBlockCalendarBlock_Entry {
        ...contentBlockCalendarBlockFragmentEvents
      }
      ... on CMS_contentBlockCategoriesTeaserBlock_Entry {
        ...contentBlockCategoriesTeaserBlockFragmentEvents
      }
      ... on CMS_contentBlockTextBlock_Entry {
        ...contentBlockTextBlockFragmentEvents
      }
      ... on CMS_contentBlockTitleBlock_Entry {
        ...contentBlockTitleBlockFragmentEvents
      }
      ... on CMS_contentBlockTitleBlockSub_Entry {
        ...contentBlockTitleBlockSubFragmentEvents
      }
      ... on CMS_contentBlockImageBlock_Entry {
        ...contentBlockImageBlockFragmentEvents
      }
      ... on CMS_contentBlockTwoColumnsBlock_Entry {
        ...contentBlockTwoColumnsBlockFragment
      }
      ... on CMS_contentBlockNewsletterBlock_Entry {
        ...contentBlockNewsletterBlockFragment
      }
    }
  }

  fragment contentBlockMenuCardBlockFragmentEvents on CMS_contentBlockMenuCardBlock_Entry {
    id
    title
    typeHandle
    menuCardDescription
    menuCardZusatz
    menuCardMenu {
      id
      ... on CMS_menus_Entry {
        id
        title
        typeHandle
        menuEntry {
          ... on CMS_menuEntryTitleBlock_Entry {
            id
            typeHandle
            menuEntryTitle
          }
          ... on CMS_menuEntryBlock_Entry {
            id
            typeHandle
            anzahlPersonen
            menuEntryLabel
            menuEntryPrice
            menuEntryText
          }
        }
      }
    }
  }

  fragment contentBlockCatBlockRoomsFragmentEvents on CMS_contentBlockCatBlockRooms_Entry {
    id
    title
    typeHandle
    overviewPages {
      ... on CMS_pages_Entry {
        ...pagesPagesFragmentEvents
      }
    }
  }

  fragment eventFragmentEvents on CMS_events_Entry {
    id
    title
    seoTitle
    url
    overviewDescription
    seoDescription
    typeHandle
    eventDateDoorsOpen
    eventdateEventBegin
    eventPerformer
    priceBookingInAdvance
    priceBoxOffice
    ticketlink
    overviewDescription
    eventDescription
    uri
    overviewImage {
      id
      title
      alt
      url
      width
      height
    }
  }

  fragment contentBlockCalendarBlockFragmentEvents on CMS_contentBlockCalendarBlock_Entry {
    id
    title
    typeHandle
    calendarDescription
    calendarEvents {
      id
      ... on CMS_events_Entry {
        ...eventFragmentEvents
      }
    }
  }

  fragment contentBlockCategoriesTeaserBlockFragmentEvents on CMS_contentBlockCategoriesTeaserBlock_Entry {
    id
    title
    typeHandle
    teaserBlockCategories {
      ... on CMS_pages_Entry {
        ...pagesPagesFragmentEvents
      }
    }
  }

  fragment contentBlockTextBlockFragmentEvents on CMS_contentBlockTextBlock_Entry {
    id
    title
    typeHandle
    textBlockText
  }

  fragment contentBlockTitleBlockFragmentEvents on CMS_contentBlockTitleBlock_Entry {
    id
    title
    typeHandle
    titleBlockTitle
  }

  fragment contentBlockTitleBlockSubFragmentEvents on CMS_contentBlockTitleBlockSub_Entry {
    id
    title
    typeHandle
    titleBlockTitleSub
  }

  fragment contentBlockImageBlockFragmentEvents on CMS_contentBlockImageBlock_Entry {
    id
    title
    typeHandle
    imageBlockImage {
      id
      title
      url
      alt
    }
  }

  fragment contentBlockTwoColumnsBlockFragment on CMS_contentBlockTwoColumnsBlock_Entry {
    id
    title
    typeHandle
    twoColumnsText
    twoColumnsImage {
      id
      title
      url
      alt
    }
    twoColumnsImgPos
  }

  fragment contentBlockNewsletterBlockFragment on CMS_contentBlockNewsletterBlock_Entry {
    id
    title
    typeHandle
    newsletterBlockText
  }

  query ($uri: String!, $siteId: CMS_QueryArgument) {
    cms {
      entry(uri: [$uri], siteId: [$siteId]) {
        id
        title
        url
        typeHandle
        siteId
        language
        uri
        localized {
          id
          url
          uri
          language
        }
        ancestors {
          id
          title
          typeHandle
          uri
        }
        ... on CMS_events_Entry {
          backgroundColor
          backgroundColorCustom
          backgroundImage {
            id
            title
            alt
            url
          }
          ...eventFragmentEvents
          ...contentBlocksFragmentEvents
        }
      }
    }
  }
`;

const Event: React.FC<EventType> = ({
  data: {
    cms: { entry },
  },
}) => {
  return (
    <DefaultLayout
      id={entry.id}
      title={entry.title}
      seoTitle={entry.seoTitle}
      url={entry.url}
      description={entry.overviewDescription}
      seoDescription={entry.seoDescription}
      image={entry.overviewImage}
      typeHandle={entry.typeHandle}
      siteId={entry.siteId}
      language={entry.language}
      backgroundColor={entry.backgroundColor}
      backgroundColorCustom={entry.backgroundColorCustom}
      backgroundImage={entry.backgroundImage}
      localized={entry.localized}
      className="default-layout"
      jsonLd={(siteUrl) => buildEventJsonLd(entry, siteUrl)}
    >
      <EventBlock
        title={entry.title}
        typeHandle={entry.typeHandle}
        eventDateDoorsOpen={entry.eventDateDoorsOpen}
        eventdateEventBegin={entry.eventdateEventBegin}
        eventPerformer={entry.eventPerformer}
        priceBookingInAdvance={entry.priceBookingInAdvance}
        priceBoxOffice={entry.priceBoxOffice}
        ticketlink={entry.ticketlink}
        calendarDescription={entry.calendarDescription}
        eventDescription={entry.eventDescription}
        overviewImage={entry.overviewImage}
        language={entry.language}
      />
      <ContentBlocks
        contentBlocks={entry.contentBlocks}
        id={entry.id}
        title={entry.title}
        typeHandle={entry.typeHandle}
        language={entry.language}
      />
    </DefaultLayout>
  );
};

export default Event;
